import { Alert, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useCatalogUploadQuery } from '../../api/hooks/catalogs';
import {
  CatalogProductAssociationResponse,
  VariantsWithPrice,
} from 'src/app/api/openapi';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import { useEffect, useRef, useState } from 'react';
import { CatalogProductsClient } from 'src/app/api/clients/catalogProducts';
import CustomGridTreeDataGroupingCell from 'src/app/components/CustomGridTreeDataGroupingCell';
import CustomDataGrid from 'src/app/components/CustomDataGrid';

const CatalogProductsView = () => {
  const { merchantId, catalogId } = useParams();
  const [products, setProducts] = useState<any[]>([]);
  const cursor = useRef<string | undefined>(undefined);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const apiRef = useGridApiRef();

  const handleFetchProducts = (page: number) =>
    CatalogProductsClient.listMerchantCatalogProducts(
      merchantId || '',
      catalogId || '',
      'product_id',
      20,
      cursor.current
    )
      .then(({ data }) => {
        cursor.current = data.cursor;
        setProducts(
          data?.items?.reduce(
            (rows: any[], item) => [
              ...rows,
              { ...item, path: [item.id || item.product.id] },
              ...item.product.variants.map((variant) => ({
                ...variant,
                path: [item.id || item.product.id, variant.id],
              })),
            ],
            []
          ) || []
        );
        setPaginationModel({ page, pageSize: 10 });
      })
      .catch(() => null);

  useEffect(() => {
    handleFetchProducts(0);
  }, []);

  const { data: uploads } = useCatalogUploadQuery({ merchantId, catalogId });

  const getVariantName = (variant: VariantsWithPrice) => {
    if (!variant || !variant.varies_by) return '';
    return variant.varies_by.map((variable) => variable.value).join(', ');
  };

  const columns: GridColDef<
    CatalogProductAssociationResponse | VariantsWithPrice
  >[] = [
    {
      field: 'title',
      headerName: 'Name',
      renderCell: ({ row }) =>
        // @ts-ignore
        row.path.length > 1
          ? getVariantName(row as VariantsWithPrice)
          : (row as CatalogProductAssociationResponse).product.title || row.id,
      flex: 2,
    },
    {
      field: 'Price',
      renderCell: ({ row }) => {
        if ('price' in row)
          return `$${
            row.price.amount ||
            (row.suggested_prices?.length && row.suggested_prices[0].amount)
          }`;
        const minPrice = row.product.variants.reduce(
          (min: number, variant: VariantsWithPrice) =>
            variant.price.amount < min ? variant.price.amount : min,
          Infinity
        );
        return minPrice !== Infinity ? `Starting at $${minPrice}` : '';
      },
      headerName: 'Price',
      flex: 1,
    },
  ];

  return (
    <>
      <Typography variant="h6">Products</Typography>
      {uploads?.items?.some(
        ({ processed_count, total_count }) => processed_count < total_count
      ) && (
        <Stack p={2} pr={4}>
          <Alert severity="warning">Processing catalog products...</Alert>
        </Stack>
      )}
      <Stack maxHeight="80vh">
        <CustomDataGrid
          apiRef={apiRef}
          rows={products}
          columns={columns}
          treeData
          pagination
          paginationMode="server"
          rowCount={-1}
          getRowId={(row) => row.path}
          getTreeDataPath={(row: any) => row.path}
          groupingColDef={{
            headerName: '',
            renderCell: (params) =>
              'product' in params.row &&
              params.row.product.variants &&
              params.row.product.variants.length ? (
                <CustomGridTreeDataGroupingCell {...params} />
              ) : null,
            width: 55,
            resizable: false,
          }}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => handleFetchProducts(model.page)}
        />
      </Stack>
    </>
  );
};

export default CatalogProductsView;
